// Server-set attribution cookies, because WebKit ITP wipes script-writable
// storage (localStorage, document.cookie) for sites reached via a decorated
// cross-site link — exactly an ad click — while a `Set-Cookie` from a
// first-party response keeps its Max-Age.
//
// The client mints the id and this route only persists it: the Worker is not
// on the page-load path, and the id must be known synchronously for the first
// event.
//
// Not HttpOnly, unlike the session cookie: the client cascade reads it
// synchronously before any network call. It is not a credential.

import { CLICK_ID_PATTERN, isValidLeadId } from "./attribution";
import { isSameOriginRequest } from "./auth/fetchMetadata";
import { json, readBodyBounded } from "./http";

export const VISITOR_ENDPOINT = "/api/visitor";

// The 90-day offline-conversion window, refreshed on every page load.
export const VISITOR_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

// `/`, not the session cookie's `/auth`: the page reads it on every route.
const VISITOR_COOKIE_PATH = "/";

const MAX_BODY_BYTES = 2 * 1024;

// No platform allowlist here: checkout re-checks KNOWN_CLICK_SOURCES.
const CLICK_SOURCE_PATTERN = /^[a-z_]{1,32}$/;

const FUTURE_CLOCK_SKEW_MS = 24 * 60 * 60 * 1000;

// Structurally identical to the client's `StoredClickId`.
export interface VisitorClick {
  source: string;
  value: string;
  at: number;
}

export interface VisitorPayload {
  leadId: string;
  click: VisitorClick | null;
}

export interface VisitorCookieNames {
  visitor: string;
  click: string;
}

export function visitorCookieNames(cookiePrefix: string): VisitorCookieNames {
  return {
    visitor: `${cookiePrefix}-visitor`,
    click: `${cookiePrefix}-click`,
  };
}

// Unescaped is safe only because `:` is in none of the three patterns. One
// cookie, not three, keeps the trio atomic.
export function encodeClickCookie(click: VisitorClick): string {
  return `${click.source}:${click.value}:${click.at}`;
}

// Untrusted input at both ends, so total: malformed shapes return null.
export function decodeClickCookie(
  raw: string | undefined | null,
): VisitorClick | null {
  if (!raw) return null;
  const parts = raw.split(":");
  if (parts.length !== 3) return null;
  const [source, value, rawAt] = parts;
  if (source === undefined || value === undefined || rawAt === undefined) {
    return null;
  }
  if (!CLICK_SOURCE_PATTERN.test(source)) return null;
  if (!CLICK_ID_PATTERN.test(value)) return null;
  // `Number`, not `parseInt`: parseInt("17e5junk") is 17, dating it to 1970.
  const at = Number(rawAt);
  if (!Number.isSafeInteger(at) || at <= 0) return null;
  return { source, value, at };
}

// Exported for the client, which mirrors a fresh lead id into
// `document.cookie` in exactly this form in case the POST never lands.
export function serializeVisitorCookie(
  name: string,
  value: string,
  maxAge: number,
  secure: boolean,
): string {
  const attrs = [
    `${name}=${value}`,
    `Path=${VISITOR_COOKIE_PATH}`,
    "SameSite=Lax",
    `Max-Age=${maxAge}`,
  ];
  if (secure) attrs.push("Secure");
  return attrs.join("; ");
}

// The remainder of the click's own window, not a fresh 90 days: a click that
// can no longer be uploaded is worse than none.
export function clickMaxAge(at: number, nowMs: number): number {
  const ageSeconds = Math.floor((nowMs - at) / 1000);
  if (ageSeconds < 0) return VISITOR_MAX_AGE_SECONDS;
  return Math.max(0, VISITOR_MAX_AGE_SECONDS - ageSeconds);
}

export function buildVisitorCookies(
  names: VisitorCookieNames,
  payload: VisitorPayload,
  secure: boolean,
  nowMs: number,
): string[] {
  const cookies = [
    serializeVisitorCookie(
      names.visitor,
      payload.leadId,
      VISITOR_MAX_AGE_SECONDS,
      secure,
    ),
  ];
  if (payload.click) {
    cookies.push(
      serializeVisitorCookie(
        names.click,
        encodeClickCookie(payload.click),
        clickMaxAge(payload.click.at, nowMs),
        secure,
      ),
    );
  }
  return cookies;
}

// A bad click is dropped alone; only a bad leadId fails the request, since
// losing the join key costs more than the click.
export function parseVisitorPayload(
  body: unknown,
  nowMs: number,
): VisitorPayload | null {
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    return null;
  }
  const raw = body as Record<string, unknown>;
  const leadId = raw.leadId;
  if (!isValidLeadId(leadId)) return null;

  const value = raw.clickId;
  const source = raw.clickSource;
  const at = raw.clickAt;
  const clickOk =
    typeof value === "string" &&
    CLICK_ID_PATTERN.test(value) &&
    typeof source === "string" &&
    CLICK_SOURCE_PATTERN.test(source) &&
    typeof at === "number" &&
    Number.isSafeInteger(at) &&
    at > 0 &&
    at <= nowMs + FUTURE_CLOCK_SKEW_MS;

  return {
    leadId,
    click: clickOk ? { source, value, at } : null,
  };
}

export interface VisitorLog {
  logWorkerEvent(
    eventName: "acquisition.visitor_cross_site" | "acquisition.visitor_invalid",
    data: Record<string, never>,
    level: "info",
  ): void;
}

export interface VisitorHandlerOptions {
  /** The app's cookie prefix: cookies are `<prefix>-visitor` and `<prefix>-click`. */
  cookiePrefix: string;
  log: VisitorLog;
}

export type VisitorHandler = (
  request: Request,
  secure: boolean,
) => Promise<Response>;

// `SameSite=Lax` governs sending, not setting: without the fetch-metadata
// gate a cross-site no-cors POST could plant an attacker's lead id or gclid.
// Old Safari (the ITP population) omits Sec-Fetch-Site but sends Origin, so
// each header covers the other's gap. Probes log at info, not error.
export function createVisitorHandler({
  cookiePrefix,
  log,
}: VisitorHandlerOptions): VisitorHandler {
  const names = visitorCookieNames(cookiePrefix);
  return async (request, secure) => {
    if (request.method !== "POST") {
      return json(405, { error: "method_not_allowed" });
    }

    if (
      !isSameOriginRequest(
        request.headers,
        new URL(request.url).origin,
        "beacon",
      )
    ) {
      log.logWorkerEvent("acquisition.visitor_cross_site", {}, "info");
      return json(403, { error: "cross_site" });
    }

    const declared = Number(request.headers.get("content-length"));
    if (Number.isFinite(declared) && declared > MAX_BODY_BYTES) {
      return json(413, { error: "payload_too_large" });
    }

    const text = await readBodyBounded(request, MAX_BODY_BYTES);
    if (text === null) {
      return json(413, { error: "payload_too_large" });
    }

    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      return json(400, { error: "invalid_json" });
    }

    const nowMs = Date.now();
    const payload = parseVisitorPayload(body, nowMs);
    if (!payload) {
      // Count only: never echo the rejected payload.
      log.logWorkerEvent("acquisition.visitor_invalid", {}, "info");
      return json(400, { error: "invalid_payload" });
    }

    const headers = new Headers({ "Cache-Control": "no-store" });
    for (const cookie of buildVisitorCookies(names, payload, secure, nowMs)) {
      headers.append("Set-Cookie", cookie);
    }
    return new Response(null, { status: 204, headers });
  };
}
